import { useEffect, useMemo, useState } from 'react';
import {
  applyGoogleConsent,
  applyGoogleConsentDefault,
  getStoredConsent,
  logConsentToServer,
  saveConsent,
} from '../../utils/cookieConsent';
import { syncTrackingByConsent } from '../../utils/trackingManager';

export default function CookieConsentBanner() {
  const [isReady, setIsReady] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [preferences, setPreferences] = useState(false);

  useEffect(() => {
    applyGoogleConsentDefault();

    const existing = getStoredConsent();
    if (existing) {
      applyGoogleConsent(existing);
      void syncTrackingByConsent(existing);
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
      setPreferences(existing.preferences);
      setIsVisible(false);
    } else {
      setIsVisible(true);
    }

    setIsReady(true);
  }, []);

  const canRender = useMemo(() => isReady && isVisible, [isReady, isVisible]);

  const acceptAll = () => {
    const consent = saveConsent({ analytics: true, marketing: true, preferences: true });
    applyGoogleConsent(consent);
    void syncTrackingByConsent(consent);
    void logConsentToServer(consent);
    setIsVisible(false);
    setShowPreferences(false);
  };

  const rejectNonEssential = () => {
    const consent = saveConsent({ analytics: false, marketing: false, preferences: false });
    applyGoogleConsent(consent);
    void syncTrackingByConsent(consent);
    void logConsentToServer(consent);
    setIsVisible(false);
    setShowPreferences(false);
  };

  const savePreferences = () => {
    const consent = saveConsent({ analytics, marketing, preferences });
    applyGoogleConsent(consent);
    void syncTrackingByConsent(consent);
    void logConsentToServer(consent);
    setIsVisible(false);
    setShowPreferences(false);
  };

  if (!canRender) {
    if (!isReady) return null;

    return (
      <button
        type="button"
        onClick={() => {
          const existing = getStoredConsent();
          if (existing) {
            setAnalytics(existing.analytics);
            setMarketing(existing.marketing);
            setPreferences(existing.preferences);
          }
          setShowPreferences(true);
          setIsVisible(true);
        }}
        className="fixed bottom-4 left-4 z-[115] h-9 px-3 rounded-full border border-[#334155] bg-[#0b1220]/95 text-[#e5e7eb] text-xs hover:bg-[#111827] transition-colors"
      >
       🍪
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 left-1/2 z-[120] w-[min(960px,calc(100%-2rem))] -translate-x-1/2 rounded-2xl border border-[#1f2937] bg-[radial-gradient(circle_at_top_left,rgba(30,41,59,0.98),rgba(2,6,23,0.98))] p-5 shadow-[0_20px_60px_rgba(0,0,0,0.65)]">
      <div className="flex flex-col gap-3">
        <div>
          <h3 className="m-0 text-[1rem] font-semibold text-[#f8fafc]">Preferências de cookies e privacidade</h3>
          <p className="mt-1 text-[0.86rem] text-[#cbd5e1]">
            Usamos cookies necessários para segurança e funcionamento. Cookies de análise e marketing só são ativados com o seu consentimento.
            Pode alterar a escolha a qualquer momento em futuras atualizações do site.
          </p>
        </div>

        {showPreferences && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 rounded-xl border border-[#334155] bg-[#020617]/70 p-3">
            <div className="rounded-lg border border-[#334155] bg-[#0b1220] p-3">
              <p className="m-0 text-sm font-medium text-[#e5e7eb]">Necessários</p>
              <p className="mt-1 text-xs text-[#94a3b8]">Sempre ativos para segurança, autenticação e operação da plataforma.</p>
            </div>

            <label className="rounded-lg border border-[#334155] bg-[#0b1220] p-3 cursor-pointer">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#e5e7eb]">Análise</span>
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              <p className="mt-1 text-xs text-[#94a3b8]">Medição de desempenho e estatísticas de utilização.</p>
            </label>

            <label className="rounded-lg border border-[#334155] bg-[#0b1220] p-3 cursor-pointer">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#e5e7eb]">Marketing</span>
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              <p className="mt-1 text-xs text-[#94a3b8]">Personalização publicitária e medição de campanhas.</p>
            </label>

            <label className="rounded-lg border border-[#334155] bg-[#0b1220] p-3 cursor-pointer">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-[#e5e7eb]">Preferências</span>
                <input
                  type="checkbox"
                  checked={preferences}
                  onChange={(e) => setPreferences(e.target.checked)}
                  className="h-4 w-4"
                />
              </div>
              <p className="mt-1 text-xs text-[#94a3b8]">Memória de opções de interface e experiência personalizada.</p>
            </label>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="m-0 text-[0.78rem] text-[#94a3b8]">
            Consulte <a href="/privacidade.html" className="text-[#a5b4fc] hover:underline">Política de Privacidade</a> e <a href="/termos.html" className="text-[#a5b4fc] hover:underline">Termos e Condições</a>.
          </p>

          <div className="flex flex-wrap items-center gap-2">
            {!showPreferences ? (
              <button
                type="button"
                onClick={() => setShowPreferences(true)}
                className="h-10 px-3 rounded-lg border border-[#334155] bg-[#0b1220] text-[#e5e7eb] text-sm hover:bg-[#111827] transition-colors"
              >
                Personalizar
              </button>
            ) : (
              <button
                type="button"
                onClick={savePreferences}
                className="h-10 px-3 rounded-lg border border-[#334155] bg-[#0b1220] text-[#e5e7eb] text-sm hover:bg-[#111827] transition-colors"
              >
                Guardar preferências
              </button>
            )}

            <button
              type="button"
              onClick={rejectNonEssential}
              className="h-10 px-3 rounded-lg border border-[#334155] bg-[#0b1220] text-[#e5e7eb] text-sm hover:bg-[#111827] transition-colors"
            >
              Rejeitar não essenciais
            </button>

            <button
              type="button"
              onClick={acceptAll}
              className="h-10 px-3 rounded-lg bg-gradient-to-br from-[#8b5cf6] to-[#ec4899] text-white text-sm font-medium hover:brightness-110 transition"
            >
              Aceitar tudo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
